/**
 * Business Plan — combined earnings estimator across multiple products.
 * Rendered as an extra section inside the Earnings Calculator tab.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, LineChart } from "lucide-react";

const inr = (n) =>
  n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN");

const FIELDS_BY_PRODUCT = {
  professional_course: ["quantity"],
  language_testprep:   ["quantity", "customer_fee"],
  inhouse_bde:         ["quantity"],
  inhouse_fs:          ["quantity"],
  inhouse_uae:         ["quantity"],
  inhouse_sac:         ["quantity"],
  inhouse_scp:         ["quantity"],
  inhouse_bilingual_de: ["quantity"],
  inhouse_bilingual_fr: ["quantity"],
  insurance_health:    ["premium"],
  insurance_life:      ["premium"],
  insurance_renewal:   ["premium"],
  loan:                ["disbursed_amount"],
  mutual_fund:         ["aum", "sip_monthly", "years"],
  pms_aif:             ["ticket_size"],
  bonds_ipo:           ["disbursed_amount"],
  indian_admission:    ["quantity", "tuition_amount"],
  study_abroad:        ["quantity", "tuition_amount"],
  mbbs_abroad:         ["quantity"],
};

const FIELD_LABEL = {
  quantity:         "Qty",
  customer_fee:     "Fee (₹)",
  premium:          "Premium (₹)",
  disbursed_amount: "Amount (₹)",
  aum:             "AUM (₹)",
  sip_monthly:     "SIP/mo (₹)",
  years:           "Years",
  ticket_size:     "Ticket (₹)",
  tuition_amount:  "Tuition (₹)",
};

export default function BusinessPlanBlock() {
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([
    { product_id: "professional_course", quantity: 5 },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/earnings/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const setLine = (i, k, v) =>
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () =>
    setLines(ls => [...ls, { product_id: "insurance_health", quantity: 1, premium: 10000 }]);
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  const run = async () => {
    setLoading(true);
    try {
      const payload = { lines: lines.map(l => ({
        product_id: l.product_id,
        quantity: parseInt(l.quantity || 1, 10),
        ...Object.fromEntries(
          Object.entries(l).filter(([k, v]) =>
            !["product_id", "quantity"].includes(k) && v !== "" && v != null,
          ).map(([k, v]) => [k, Number(v)]),
        ),
      })) };
      const r = await api.post("/earnings/business-plan", payload);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-5 space-y-3" data-testid="business-plan-card">
      <div className="flex items-center gap-2">
        <LineChart size={18}/>
        <h3 className="text-lg font-semibold">Business Plan — Combined earnings</h3>
      </div>
      <div className="text-xs text-slate-500">
        Estimate your monthly / annual earnings from several products at once.
      </div>

      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex flex-wrap gap-2 items-end p-2 border rounded"
               data-testid={`plan-line-${i}`}>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Product</Label>
              <Select value={l.product_id}
                      onValueChange={v => setLine(i, "product_id", v)}>
                <SelectTrigger className="h-8"
                               data-testid={`plan-product-${i}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.product_id} value={p.product_id}>
                      {p.product_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(FIELDS_BY_PRODUCT[l.product_id] || ["quantity"]).map(k => (
              <div key={k} className="w-24">
                <Label className="text-xs">{FIELD_LABEL[k] || k}</Label>
                <Input className="h-8" type="number"
                       data-testid={`plan-field-${i}-${k}`}
                       value={l[k] ?? ""}
                       onChange={e => setLine(i, k, e.target.value)}/>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => removeLine(i)}
                    data-testid={`plan-remove-${i}`}>
              <Trash2 size={14}/>
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addLine}
                  data-testid="plan-add-line">
            <Plus size={14} className="mr-1"/> Add row
          </Button>
          <Button size="sm" onClick={run} disabled={loading}
                  data-testid="plan-calculate">
            {loading ? <Loader2 className="animate-spin" size={14}/> : "Calculate"}
          </Button>
        </div>
      </div>

      {result && (
        <Card className="p-4 bg-slate-50 mt-3" data-testid="plan-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="One-time gross" value={inr(result.totals.one_time_gross)}/>
            <Stat label="Estimated TDS" value={inr(result.totals.one_time_tds)}/>
            <Stat label="One-time net payout"
                  value={inr(result.totals.one_time_net_payout)} highlight/>
            <Stat label="Recurring annual"
                  value={inr(result.totals.recurring_annual_gross)}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Stat label="Year 1" value={inr(result.totals.year_1_total_gross)}/>
            <Stat label="Year 3" value={inr(result.totals.year_3_total_gross)}/>
            <Stat label="Year 5" value={inr(result.totals.year_5_total_gross)}/>
            <Stat label="Year 10" value={inr(result.totals.year_10_total_gross)}/>
          </div>
          <div className="mt-3 pt-3 border-t text-xs">
            <div className="font-medium mb-1">Product-wise breakdown</div>
            <table className="w-full">
              <thead className="text-slate-500">
                <tr className="text-left">
                  <th>Product</th><th>Qty</th>
                  <th className="text-right">One-time</th>
                  <th className="text-right">Recurring / yr</th>
                  <th className="text-right">Net payout</th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1">{l.product_name}</td>
                    <td>{l.quantity}</td>
                    <td className="text-right">{inr(l.one_time_gross)}</td>
                    <td className="text-right">{inr(l.recurring_annual)}</td>
                    <td className="text-right font-medium">{inr(l.indicative_net_payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-400 mt-2">{result.disclaimer}</div>
        </Card>
      )}
    </Card>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${highlight ? "text-emerald-700" : ""}`}>{value}</div>
    </div>
  );
}
